import {FC, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import Button from 'components/Button';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import Stacked from 'components/Layout/Stacked';
import ModalDestructiveConfirm from 'components/ModalDestructiveConfirm';
import Pill from 'components/Pill';
import SectionHeader from 'components/SectionHeader';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import {onApiFormError, onApiKeySuccess} from 'utils/request/result';
import {
    useAddNewEmailLink,
    useDeleteEmail,
    useSendConfirmation,
    useSetPrimary,
} from '../services';
import {Email, ProfileEmailSettingsBackend} from '../types';

type AddNewFormData = FormFields<{
    address: string;
}>;

type EmailAddressesProps = {
    emails: Array<Email>;
    isSfConnected: boolean;
};

const EmailAddresses: FC<EmailAddressesProps> = ({emails, isSfConnected}) => {
    const {checkFlag, refreshData} = useBackend<ProfileEmailSettingsBackend>();

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [target, setTarget] = useState('');

    const [confirmationLinksSent, setConfirmationLinksSent] = useState<
        Record<string, boolean>
    >({});

    const methods = useForm<AddNewFormData>();
    const {clearErrors, handleSubmit, reset} = methods;

    const deleteEmail = useDeleteEmail();
    const sendConfirmation = useSendConfirmation();
    const setPrimary = useSetPrimary();
    const addNew = useAddNewEmailLink();

    const emailAddress = useMemo(
        () => emails.find(({mid}) => mid === target)?.email,
        [emails, target],
    );

    const onDeleteEmail = (id: string) => {
        clearErrors();
        setTarget(id);
        setConfirmDelete(true);
    };

    const onConfirmDelete = () => {
        setConfirmDelete(false);
        deleteEmail
            .mutateAsync({mid: target})
            .then(onApiKeySuccess(methods))
            .catch(onApiFormError(methods))
            .finally(() => {
                refreshData(window.Urls.email_settings_view());
                setTarget('');
            });
    };

    const onSendConfirmation = (email_address: string) => {
        clearErrors();
        setTarget(email_address);
        sendConfirmation
            .mutateAsync({email_address})
            .then((response) => {
                onApiKeySuccess(
                    methods,
                    'emailAddressSuccessMessage',
                )(response);
                setConfirmationLinksSent((prev) => ({
                    ...prev,
                    [email_address]: true,
                }));
            })
            .catch(onApiFormError(methods, 'emailAddressNonFieldErrors'))
            .finally(() => {
                setTarget('');
            });
    };

    const onSetPrimary = (email: string) => {
        clearErrors();
        setTarget(email);
        setPrimary
            .mutateAsync({email})
            .then(onApiKeySuccess(methods))
            .catch(onApiFormError(methods))
            .finally(() => {
                refreshData(window.Urls.email_settings_view());
                setTarget('');
            });
    };

    const onAddNew = async ({address}: AddNewFormData) => {
        await addNew
            .mutateAsync({address})
            .then((response) => {
                reset();
                onApiKeySuccess(methods)(response);
            })
            .catch(onApiFormError(methods))
            .finally(() => refreshData(window.Urls.email_settings_view()));
    };

    const withEnforceEmailVerificationHeaderRow = () => (
        <TableStatic.HeaderRow>
            <TableStatic.Column>{t`Email`}</TableStatic.Column>
            <TableStatic.Column>{t`Status`}</TableStatic.Column>
            <TableStatic.Column colSpan={3} />
        </TableStatic.HeaderRow>
    );

    const headerRow = () => (
        <TableStatic.HeaderRow>
            <TableStatic.Column>{t`Email`}</TableStatic.Column>
            <TableStatic.Column>{t`Verified`}</TableStatic.Column>
            <TableStatic.Column colSpan={2} />
        </TableStatic.HeaderRow>
    );

    const withEnforceEmailVerificationTableRow = ({
        email,
        isDeleteRestricted,
        isPrimary,
        isVerified,
        mid,
        permissions: {canDelete, canSetAsPrimary},
    }: Email) => (
        <>
            <TableStatic.Cell>{email}</TableStatic.Cell>
            <TableStatic.Cell>
                {isVerified && (
                    <span className="flex items-center gap-1 text-light-primary">
                        <Icon
                            className="text-green-700"
                            icon="success"
                            size="base"
                        />
                        {t`Verified`}
                    </span>
                )}
                {!isVerified && (
                    <span className="flex items-center gap-1 text-coral-700">
                        <Icon icon="warning" size="base" />
                        {t`Unverified`}
                    </span>
                )}
            </TableStatic.Cell>
            <TableStatic.Cell>
                {!isVerified && (
                    <Button
                        aria-label="send-email-confirmation-link"
                        disabled={confirmationLinksSent[mid]}
                        isLoading={sendConfirmation.isLoading && target === mid}
                        onClick={() => onSendConfirmation(mid)}
                        size="xs"
                    >
                        {t`Send Verification Email`}
                    </Button>
                )}
            </TableStatic.Cell>
            <TableStatic.Cell>
                {isPrimary && <Pill color="grey" label={t`Primary`} />}
                {!isPrimary && !isSfConnected && (
                    <TooltipWrapper
                        href={canSetAsPrimary.action}
                        message={canSetAsPrimary.failureReason}
                    >
                        <Button
                            aria-label="set-as-primary"
                            disabled={isPrimary || !canSetAsPrimary.allowed}
                            isLoading={setPrimary.isLoading && target === mid}
                            kind="secondary"
                            onClick={() => onSetPrimary(mid)}
                            size="xs"
                        >
                            {t`Set as primary`}
                        </Button>
                    </TooltipWrapper>
                )}
            </TableStatic.Cell>
            <TableStatic.Cell className="flex flex-row-reverse">
                {(!isSfConnected || !isDeleteRestricted) && (
                    <TooltipWrapper message={canDelete.failureReason}>
                        <Button
                            aria-label="delete"
                            data-testid={`delete:${mid}`}
                            disabled={!canDelete.allowed}
                            isLoading={deleteEmail.isLoading && target === mid}
                            kind="destructive"
                            onClick={() => onDeleteEmail(mid)}
                            size="xs"
                        >
                            {t`Delete`}
                        </Button>
                    </TooltipWrapper>
                )}
            </TableStatic.Cell>
        </>
    );

    const tableRow = ({
        email,
        isDeleteRestricted,
        isPrimary,
        isVerified,
        mid,
        permissions: {canDelete, canSetAsPrimary},
    }: Email) => (
        <>
            <TableStatic.Cell>{email}</TableStatic.Cell>
            <TableStatic.Cell>
                {isVerified && (
                    <div className="flex items-center gap-1 text-light-primary">
                        <Icon
                            className="text-green-700"
                            icon="success"
                            size="base"
                        />
                        <span>{t`Verified`}</span>
                    </div>
                )}
                {!isVerified && (
                    <Button
                        aria-label="send-email-confirmation-link"
                        disabled={confirmationLinksSent[mid]}
                        isLoading={sendConfirmation.isLoading && target === mid}
                        onClick={() => onSendConfirmation(mid)}
                        size="xs"
                    >
                        {t`Send Verification Email`}
                    </Button>
                )}
            </TableStatic.Cell>
            <TableStatic.Cell>
                {isPrimary && <Pill color="grey" label={t`Primary`} />}
                {!isPrimary && !isSfConnected && (
                    <TooltipWrapper
                        href={canSetAsPrimary.action}
                        message={canSetAsPrimary.failureReason}
                    >
                        <Button
                            aria-label="set-as-primary"
                            disabled={isPrimary || !canSetAsPrimary.allowed}
                            isLoading={setPrimary.isLoading && target === mid}
                            kind="secondary"
                            onClick={() => onSetPrimary(mid)}
                            size="xs"
                        >
                            {t`Set as primary`}
                        </Button>
                    </TooltipWrapper>
                )}
            </TableStatic.Cell>
            <TableStatic.Cell>
                {(!isSfConnected || !isDeleteRestricted) && (
                    <TooltipWrapper message={canDelete.failureReason}>
                        <Button
                            aria-label="delete"
                            data-testid={`delete:${mid}`}
                            disabled={!canDelete.allowed}
                            isLoading={deleteEmail.isLoading && target === mid}
                            kind="destructive"
                            onClick={() => onDeleteEmail(mid)}
                            size="xs"
                        >
                            {t`Delete`}
                        </Button>
                    </TooltipWrapper>
                )}
            </TableStatic.Cell>
        </>
    );

    return (
        <div className="mb-4">
            <SectionHeader
                className="mb-2"
                hideHR={true}
                level={2}
                subtitle={
                    // TODO: 2024-02-29: Check if this can be removed
                    checkFlag('enforce_email_verification')
                        ? t`The "Primary" address can be used for login and is where user notifications and password reset requests are sent.`
                        : t`Your primary address is for user notifications or password reset requests. Any of the following email addresses can be set as a Notification Address below.`
                }
                title={t`Email Addresses`}
            />
            <FormProvider {...methods}>
                <TableStatic aria-label={t`Email Addresses`} className="mb-4">
                    <TableStatic.Header>
                        {
                            // TODO: 2024-02-29: Check if this can be removed
                            checkFlag('enforce_email_verification')
                                ? withEnforceEmailVerificationHeaderRow()
                                : headerRow()
                        }
                    </TableStatic.Header>
                    <TableStatic.Body>
                        {emails.map((email) => (
                            <TableStatic.Row key={email.mid}>
                                {
                                    // TODO: 2024-02-29: Check if this can be removed
                                    checkFlag('enforce_email_verification')
                                        ? withEnforceEmailVerificationTableRow(
                                              email,
                                          )
                                        : tableRow(email)
                                }
                            </TableStatic.Row>
                        ))}
                    </TableStatic.Body>
                </TableStatic>
                <FormResult className="mt-2" prefix="emailAddress" />
                <form onSubmit={handleSubmit(onAddNew)}>
                    <SectionHeader
                        className="mt-6 mb-2"
                        hideHR={true}
                        level={2}
                        subtitle={t`A Verification Email will be sent automatically when you add a new email.`}
                        title={t`Add a new email`}
                    />
                    <Stacked>
                        <Field
                            helpText={t`e.g. name@example.com`}
                            label={t`Add Email Address`}
                            name="address"
                            type="input"
                        >
                            <div className="flex items-start gap-2">
                                <InputEmail
                                    className="max-w-sm flex-1"
                                    name="address"
                                />
                                <SubmitButton
                                    aria-label="add-email"
                                    kind="secondary"
                                >
                                    {t`Add`}
                                </SubmitButton>
                            </div>
                        </Field>
                    </Stacked>
                </form>
                <FormResult className="mt-2" />
                <ModalDestructiveConfirm
                    cancel={t`Go back`}
                    header={t`Delete Email Address`}
                    isOpen={confirmDelete}
                    onCancel={setConfirmDelete}
                    onConfirm={onConfirmDelete}
                >
                    {jt`Are you sure you want to delete this email address ${emailAddress}?`}
                </ModalDestructiveConfirm>
            </FormProvider>
        </div>
    );
};

export default EmailAddresses;
